var collectionsFilteringIndexCache = {};

function collectionsFilteringClean(value) {
  return String(value || "").trim();
}

function collectionsFilteringKey(value) {
  return collectionsFilteringClean(value).toLowerCase();
}

function collectionsFilteringCollectionHandle(url) {
  var match = collectionsFilteringClean(url).match(/\/collections\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

function collectionsFilteringUniqueSorted(rows, prop) {
  var seen = new Map();

  rows.forEach(function (row) {
    var value = collectionsFilteringClean(row[prop]);
    if (value) {
      seen.set(collectionsFilteringKey(value), value);
    }
  });

  return Array.from(seen.values()).sort(function (a, b) {
    return a.localeCompare(b, undefined, { numeric: true });
  });
}

function collectionsFilteringNormalizeIndexRow(row) {
  var year = collectionsFilteringClean(row.year || row.year_from);
  var collectionHandles = Array.isArray(row.collection_handles)
    ? row.collection_handles
    : collectionsFilteringClean(row.collection_handles).split("|");

  return {
    productHandle: collectionsFilteringClean(row.product_handle || row.handle),
    productTitle: collectionsFilteringClean(row.product_title || row.title),
    productType: collectionsFilteringClean(row.product_type || row.type),
    vehicleType: collectionsFilteringClean(row.vehicle_type || row.vehicleType),
    make: collectionsFilteringClean(row.make),
    model: collectionsFilteringClean(row.model),
    year: year,
    collectionHandles: collectionHandles.map(collectionsFilteringClean).filter(Boolean)
  };
}

function collectionsFilteringLoadIndex(url) {
  if (!url) {
    return Promise.resolve({ fitments: [] });
  }

  if (!collectionsFilteringIndexCache[url]) {
    collectionsFilteringIndexCache[url] = fetch(url, { credentials: "same-origin" })
      .then(function (response) {
        if (!response.ok) {
          throw new Error("Could not load YMM index");
        }
        return response.json();
      })
      .then(function (payload) {
        payload.fitments = (payload.fitments || []).map(collectionsFilteringNormalizeIndexRow);
        return payload;
      });
  }

  return collectionsFilteringIndexCache[url];
}

function collectionsFilteringLoadIndexes(urls) {
  return Promise.all(
    urls.filter(Boolean).map(function (url) {
      return collectionsFilteringLoadIndex(url).catch(function () {
        return { fitments: [] };
      });
    })
  ).then(function (payloads) {
    return {
      fitments: payloads.reduce(function (rows, payload) {
        return rows.concat(payload.fitments || []);
      }, []),
    };
  });
}

if (!customElements.get("collections-filtering")) {
  class CollectionsFiltering extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form");
      this.button = this.querySelector('button[type="submit"]');
      this.select = this.querySelector("select.dropdown-collection");
      this.input = this.querySelector("input.input-collection");
      this.selects = this.querySelectorAll("select.dropdown-filter");
      this.filterMode = this.dataset.filterMode || "native";
      this.indexScope = this.dataset.indexScope || "all";
      this.indexRows = [];

      if (this.filterMode === "ymm_index") {
        this.setupYmmIndexMode();
        return;
      }

      this.setupEventListeners();

      // There's no collection dropdown so we either need to populate
      // the filters of the entire store or the single collection chosen
      // (if only one collection has been chosen).
      if (!this.select) {
        this.populateCollectionFilters(true);
      }
    }

    setupYmmIndexMode() {
      this.form.addEventListener("submit", this.onYmmSubmit.bind(this));
      this.select?.addEventListener("change", this.onYmmCollectionChange.bind(this));
      this.selects.forEach((select) => {
        select.addEventListener("change", this.onYmmFilterChange.bind(this));
      });

      this.button.disabled = true;
      this.toggleLoading();

      const indexUrls = [this.dataset.indexUrl]
        .concat(collectionsFilteringClean(this.dataset.extraIndexUrl).split(",").map(collectionsFilteringClean))
        .filter(Boolean);

      collectionsFilteringLoadIndexes(indexUrls)
        .then((payload) => {
          this.indexRows = payload.fitments || [];
          this.toggleLoading();
          this.populateYmmControls();
        })
        .catch(() => {
          this.toggleLoading();
          this.button.disabled = true;
        });
    }

    setupEventListeners() {
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        window.location = this.form.getAttribute("action");
      });
      this.select?.addEventListener("change", this.onCollectionChange.bind(this));
      this.selects.forEach((event) => {
        event.addEventListener("change", this.onFilterChange.bind(this));
      });
    }

    constructURL() {
      const collectionUrl =
        this.select?.value ?? this.input?.value ?? window.routes.all_products_url;
      const url = new URL(collectionUrl, window.requestShopUrl);

      this.querySelectorAll("select.dropdown-filter:enabled").forEach((select) => {
        if (select.value === "") {
          return;
        }

        Array.from(select.selectedOptions).forEach((option) => {
          url.searchParams.set(option.dataset.paramName, option.value);
        });
      });

      return url.toString();
    }

    setFormUrl(url, background) {
      this.form.setAttribute("action", url);

      // This operation happens on the background
      // where the user should not be notified of loading with spinners
      // etc.
      if (!background) {
        this.button.disabled = false;
      }
    }

    selectRemoveOptions(select) {
      const options = select.querySelectorAll("option:not(.default)");

      options.forEach((option) => {
        select.removeChild(option);
      });

      select.querySelector("option").selected = true;
    }

    getYmmSelect(label) {
      return Array.from(this.selects).find((select) => {
        return collectionsFilteringKey(select.dataset.label) === collectionsFilteringKey(label);
      });
    }

    getYmmState() {
      const vehicleType = this.getYmmSelect("Vehicle Type") || this.getYmmSelect("Type");
      const make = this.getYmmSelect("Make");
      const model = this.getYmmSelect("Model");
      const year = this.getYmmSelect("Year");

      return {
        vehicleType: vehicleType?.value || "",
        make: make?.value || "",
        model: model?.value || "",
        year: year?.value || "",
        collection: this.getYmmCollectionHandle()
      };
    }

    getYmmCollectionHandle() {
      if (this.indexScope === "all") {
        return "";
      }

      return collectionsFilteringCollectionHandle(
        this.select?.value ?? this.input?.value ?? window.routes.all_products_url,
      );
    }

    getYmmBaseRows() {
      const collection = this.getYmmCollectionHandle();

      return this.indexRows.filter((row) => {
        if (!row.make || !row.model || !row.year) {
          return false;
        }

        if (!collection) {
          return true;
        }

        return row.collectionHandles.length === 0 || row.collectionHandles.indexOf(collection) !== -1;
      });
    }

    setYmmOptions(select, values, selectedValue, disabled) {
      if (!select) {
        return "";
      }

      const selected = collectionsFilteringClean(selectedValue);
      const defaultOption = select.querySelector("option.default");
      const placeholder = defaultOption ? defaultOption.textContent : select.dataset.label;
      const safeValues = values.map(String);

      select.innerHTML = "";
      const option = document.createElement("option");
      option.className = "default";
      option.value = "";
      option.selected = true;
      option.disabled = true;
      option.textContent = placeholder;
      select.append(option);

      safeValues.forEach((value) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = value;
        if (value === selected) {
          option.selected = true;
        }
        select.append(option);
      });

      select.disabled = Boolean(disabled || safeValues.length === 0);
      select.closest(".form-group")?.classList.toggle("disabled", select.disabled);

      if (selected && safeValues.indexOf(selected) === -1) {
        select.value = "";
      }

      return select.value;
    }

    populateYmmControls() {
      const state = this.getYmmState();
      const baseRows = this.getYmmBaseRows();
      const vehicleType = this.getYmmSelect("Vehicle Type") || this.getYmmSelect("Type");
      const make = this.getYmmSelect("Make");
      const model = this.getYmmSelect("Model");
      const year = this.getYmmSelect("Year");

      const vehicleTypeValue = this.setYmmOptions(
        vehicleType,
        collectionsFilteringUniqueSorted(baseRows, "vehicleType"),
        state.vehicleType,
        false,
      );

      const typeRows = baseRows.filter((row) => {
        return !vehicleTypeValue || collectionsFilteringKey(row.vehicleType) === collectionsFilteringKey(vehicleTypeValue);
      });

      const makeValue = this.setYmmOptions(
        make,
        collectionsFilteringUniqueSorted(typeRows, "make"),
        state.make,
        Boolean(vehicleType && !vehicleTypeValue),
      );

      const modelRows = typeRows.filter((row) => {
        return makeValue && collectionsFilteringKey(row.make) === collectionsFilteringKey(makeValue);
      });
      const modelValue = this.setYmmOptions(
        model,
        collectionsFilteringUniqueSorted(modelRows, "model"),
        state.model,
        !makeValue,
      );

      const yearRows = modelRows.filter((row) => {
        return modelValue && collectionsFilteringKey(row.model) === collectionsFilteringKey(modelValue);
      });
      this.setYmmOptions(
        year,
        collectionsFilteringUniqueSorted(yearRows, "year").sort((a, b) => Number(b) - Number(a)),
        state.year,
        !makeValue || !modelValue,
      );

      this.button.disabled = !this.getYmmSelect("Make")?.value;
      this.setFormUrl(this.constructYmmResultsUrl(), true);
    }

    onYmmCollectionChange() {
      this.selects.forEach((select) => {
        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });

      this.populateYmmControls();
    }

    onYmmFilterChange(event) {
      const selectedIndex = Number(event.target.dataset.index);

      this.selects.forEach((select) => {
        if (Number(select.dataset.index) <= selectedIndex) {
          return;
        }

        select.value = "";
      });

      this.populateYmmControls();
      this.setFormUrl(this.constructYmmResultsUrl());
    }

    constructYmmResultsUrl() {
      const state = this.getYmmState();
      const resultsUrl = this.dataset.resultsUrl || "/pages/ymm-results";
      const url = new URL(resultsUrl, window.requestShopUrl || window.location.origin);

      if (state.vehicleType) {
        url.searchParams.set("ymm_vehicle_type", state.vehicleType);
      }

      if (state.make) {
        url.searchParams.set("ymm_make", state.make);
      }

      if (state.model) {
        url.searchParams.set("ymm_model", state.model);
      }

      if (state.year) {
        url.searchParams.set("ymm_year", state.year);
      }

      if (state.collection) {
        url.searchParams.set("ymm_collection", state.collection);
      }

      return url.toString();
    }

    onYmmSubmit(event) {
      event.preventDefault();
      window.location = this.constructYmmResultsUrl();
    }

    onFilterChange(event) {
      const selectedIndex = event.target.dataset.index;

      this.selects.forEach((select) => {
        if (Number(select.dataset.index) <= Number(selectedIndex)) {
          return;
        }

        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });

      this.setFormUrl(this.constructURL());

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");
      this.toggleLoading();

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }

          return response.text();
        })
        .then((text) => {
          this.toggleLoading();
          const resultsMarkup = new DOMParser().parseFromString(text, "text/html");

          this.selects.forEach((select) => {
            if (Number(select.dataset.index) <= Number(selectedIndex)) {
              return;
            }

            const options = resultsMarkup.querySelectorAll(
              `select[data-label="${select.dataset.label}"] option`,
            );
            if (options.length > 0) {
              const availableOptions = Array.from(options).filter(
                (option) => Number(option.dataset.count) > 0,
              );
              select.append(...availableOptions);
              select.querySelector("option.default").selected = true;
            }

            if (Number(select.dataset.index) === Number(selectedIndex) + 1) {
              select.disabled = false;
              select.closest(".form-group").classList.remove("disabled");
            }
          });
        })
        .catch((error) => {
          throw error;
        });
    }

    populateCollectionFilters(background = false) {
      this.setFormUrl(this.constructURL(), background);

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");

      // When we initially populate the filters we don't want
      // to show a loading spinner.
      if (!background) {
        this.toggleLoading();
      }

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }

          return response.text();
        })
        .then((text) => {
          if (!background) {
            this.toggleLoading();
          }

          const resultsMarkup = new DOMParser().parseFromString(text, "text/html");

          this.selects.forEach((select) => {
            const options = resultsMarkup.querySelectorAll(
              `select[data-label="${select.dataset.label}"] option`,
            );
            if (options.length > 0) {
              select.append(...options);
              select.querySelector("option.default").selected = true;
            }

            if (Number(select.dataset.index) === 1) {
              select.disabled = false;
              select.closest(".form-group").classList.remove("disabled");
            }
          });
        })
        .catch((error) => {
          throw error;
        });
    }

    onCollectionChange() {
      this.selects.forEach((select) => {
        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest(".form-group").classList.add("disabled");
      });

      this.populateCollectionFilters();
    }

    toggleLoading() {
      this.classList.toggle("is-loading");
      this.button.classList.toggle("loading");
    }
  }

  customElements.define("collections-filtering", CollectionsFiltering);
}
